import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaArrowRight, FaChevronDown, FaSearch, FaTimes } from "react-icons/fa";
import { SERVICES_DATA } from "../../constant/Services";
import Badge from "../common/Badge";
import Eyebrow from "../common/Eyebrow";
import Reveal from "../common/Reveal";
import faqHeroImg from "../../assets/Services/24support.webp";

const FAQPage = () => {
  const [openFaqs, setOpenFaqs] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");

  const toggleFaq = (key) => {
    setOpenFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Flatten all FAQs from all services
  const allFAQs = Object.entries(SERVICES_DATA).flatMap(([key, service]) => {
    if (service.faq && Array.isArray(service.faq)) {
      return service.faq.map((faq) => ({
        service: service.title,
        serviceKey: key,
        question: faq.question,
        answer: faq.answer,
      }));
    }
    return [];
  });

  // Group FAQs by service
  const groupedFAQs = {};
  allFAQs.forEach((faq) => {
    if (!groupedFAQs[faq.serviceKey]) {
      groupedFAQs[faq.serviceKey] = [];
    }
    groupedFAQs[faq.serviceKey].push(faq);
  });

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  const visibleGroupedFAQs = useMemo(() => {
    if (!isSearching) return groupedFAQs;
    const filtered = {};
    Object.entries(groupedFAQs).forEach(([key, faqs]) => {
      const matches = faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(trimmedQuery) ||
          faq.answer.toLowerCase().includes(trimmedQuery)
      );
      if (matches.length) filtered[key] = matches;
    });
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedQuery, isSearching]);

  const totalMatches = Object.values(visibleGroupedFAQs).reduce(
    (sum, faqs) => sum + faqs.length,
    0
  );

  // Generate JSON-LD for FAQPage schema
  const jsonLdFAQ = () => {
    const mainEntity = allFAQs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    }));

    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: mainEntity,
    });
  };

  return (
    <>
      <Helmet>
        <title>
          FAQ - QuantaRock | Frequently Asked Questions on IT Services
        </title>
        <meta
          name="description"
          content="Get answers to frequently asked questions about QuantaRock's IT services including data center colocation, dedicated servers, VPS, cloud services, and more."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://quantarock.in/resources/faq" />
      </Helmet>
      <script type="application/ld+json">{jsonLdFAQ()}</script>

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-[var(--surface-dark)] py-20">
        <img
          src={faqHeroImg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(var(--surface-dark-rgb),0.75)] via-[rgba(var(--surface-dark-rgb),0.55)] to-[rgba(var(--surface-dark-rgb),0.35)]" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full bg-[rgba(var(--website-secondary-rgb),0.1)] blur-3xl animate-[float-slow_14s_ease-in-out_infinite]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-[rgba(var(--website-secondary-rgb),0.1)] blur-3xl animate-[float-slow_18s_ease-in-out_infinite_reverse]"
        />
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
          <Badge>Support</Badge>
          <h1
            style={{
              textShadow:
                "0 0 1px rgba(0,0,0,1), 0 0 3px rgba(0,0,0,1), 0 1px 4px rgba(0,0,0,0.95), 0 2px 10px rgba(0,0,0,0.9), 0 6px 20px rgba(0,0,0,0.7)",
            }}
            className="text-4xl font-bold leading-[1.1] text-white sm:text-5xl md:text-6xl"
          >
            Frequently Asked Questions
          </h1>
          <p
            style={{
              textShadow:
                "0 0 1px rgba(0,0,0,1), 0 0 2px rgba(0,0,0,1), 0 1px 3px rgba(0,0,0,0.9), 0 4px 12px rgba(0,0,0,0.75)",
            }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Find answers to common questions about QuantaRock services.
          </p>

          <div className="relative mx-auto mt-8 max-w-xl">
            <FaSearch
              size={16}
              className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions — e.g. colocation, uptime, pricing..."
              className="w-full rounded-full border border-white/15 bg-white/10 py-4 pl-12 pr-12 text-sm text-white placeholder:text-slate-400 backdrop-blur-md transition focus:border-[var(--website-secondary-color)] focus:bg-white/15 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--website-secondary-rgb),0.4)] sm:text-base"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-white"
              >
                <FaTimes size={16} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Jump nav */}
      <nav
        aria-label="FAQ categories"
        className="sticky top-20 md:top-24 z-30 border-b border-white/10 bg-[rgba(var(--surface-dark-rgb),0.95)] px-6 py-3 backdrop-blur-md md:py-5"
      >
        {/* The mask fades the right edge so it's clear the row scrolls. Pills
            pack from the left, so with only a few categories the fade lands on
            empty space and stays invisible. Dropped at md, where the row wraps. */}
        <div className="mx-auto flex max-w-6xl flex-nowrap gap-2 overflow-x-auto [-webkit-mask-image:linear-gradient(to_right,black_calc(100%_-_2.5rem),transparent)] [mask-image:linear-gradient(to_right,black_calc(100%_-_2.5rem),transparent)] md:flex-wrap md:justify-center md:overflow-x-visible md:[-webkit-mask-image:none] md:[mask-image:none]">
          {isSearching ? (
            <p className="text-sm font-medium text-slate-300">
              {totalMatches
                ? `${totalMatches} result${totalMatches === 1 ? "" : "s"} for "${searchQuery.trim()}"`
                : `No results for "${searchQuery.trim()}"`}
            </p>
          ) : (
            Object.entries(groupedFAQs).map(([serviceKey]) => (
              <a
                key={serviceKey}
                href={`#faq-${serviceKey}`}
                className="flex-shrink-0 whitespace-nowrap rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 transition duration-200 hover:border-[var(--website-secondary-color)] hover:text-[var(--website-secondary-color)] hover:shadow-sm md:hover:-translate-y-0.5"
              >
                {SERVICES_DATA[serviceKey]?.title || serviceKey}
              </a>
            ))
          )}
        </div>
      </nav>

      {/* FAQ Content */}
      <section className="relative overflow-hidden bg-[var(--surface-dark)] px-6 pb-20 pt-16">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/3 h-96 w-96 -translate-x-1/2 rounded-full bg-[rgba(var(--website-secondary-rgb),0.05)] blur-3xl"
        />
        <div className="relative mx-auto max-w-6xl">
          {isSearching && totalMatches === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg font-semibold text-white">
                No matching questions found
              </p>
              <p className="mt-2 text-slate-300">
                Try a different keyword, or{" "}
                <Link
                  to="/contact"
                  className="font-semibold text-[var(--website-secondary-color)] underline decoration-[rgba(var(--website-secondary-rgb),0.5)] underline-offset-2"
                >
                  ask our team directly
                </Link>
                .
              </p>
            </div>
          )}
          {Object.entries(visibleGroupedFAQs).map(([serviceKey, faqs]) => (
            <div
              key={serviceKey}
              id={`faq-${serviceKey}`}
              className="mb-16 scroll-mt-40"
            >
              <Reveal>
                <h2 className="mb-8 border-b-2 border-[var(--website-secondary-color)] pb-4 text-2xl font-bold text-white md:text-3xl">
                  {SERVICES_DATA[serviceKey]?.title || serviceKey}
                </h2>
              </Reveal>

              <div className="space-y-4">
                {faqs.map((faq, faqIndex) => {
                  const key = `${serviceKey}-${faqIndex}`;
                  const isOpen = isSearching || openFaqs.has(key);
                  return (
                    <Reveal
                      key={faqIndex}
                      delay={(faqIndex % 5) * 0.05}
                    >
                      <div
                        className={`overflow-hidden rounded-xl border shadow-sm transition-all duration-300 ${
                          isOpen
                            ? "border-[rgba(var(--website-secondary-rgb),0.6)] bg-[rgba(var(--website-secondary-rgb),0.1)] shadow-md shadow-[rgba(var(--website-secondary-rgb),0.1)]"
                            : "border-white/10 bg-white/5 hover:border-[rgba(var(--website-secondary-rgb),0.4)] hover:bg-white/10"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleFaq(key)}
                          aria-expanded={isOpen}
                          aria-controls={`faq-answer-${key}`}
                          className="flex w-full items-start gap-4 p-6 text-left"
                        >
                          <span
                            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors duration-300 ${
                              isOpen
                                ? "bg-[var(--website-secondary-color)] text-[var(--website-primary-color)]"
                                : "bg-white/10 text-[var(--website-secondary-color)]"
                            }`}
                          >
                            {String(faqIndex + 1).padStart(2, "0")}
                          </span>
                          <h3 className="flex-1 pt-1 text-lg font-semibold text-white">
                            {faq.question}
                          </h3>
                          <span
                            className={`mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                              isOpen
                                ? "rotate-180 bg-[var(--website-secondary-color)] text-[var(--website-primary-color)]"
                                : "bg-white/10 text-slate-300"
                            }`}
                          >
                            <FaChevronDown size={12} />
                          </span>
                        </button>
                        <div
                          id={`faq-answer-${key}`}
                          className={`grid transition-all duration-300 ease-in-out ${
                            isOpen
                              ? "grid-rows-[1fr] opacity-100"
                              : "grid-rows-[0fr] opacity-0"
                          }`}
                        >
                          <div className="overflow-hidden">
                            <p className="ml-[4.25rem] mr-6 mb-6 border-l-2 border-[rgba(var(--website-secondary-rgb),0.5)] pl-4 leading-relaxed text-slate-300">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-slate-50 px-6 py-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-[rgba(var(--website-secondary-rgb),0.1)] blur-3xl"
        />
        <div className="relative mx-auto grid max-w-5xl items-center gap-10 md:grid-cols-2">
          <Reveal direction="left">
            <div className="text-center md:text-left">
              <Eyebrow>Still have questions</Eyebrow>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
                Didn't find your answer?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-600 md:mx-0">
                Contact our support team or reach out for a personalized
                consultation.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-[var(--website-primary-color)] px-8 py-4 text-sm font-semibold text-white shadow-lg transition hover:scale-[1.02] hover:brightness-110"
              >
                Get in touch
              </Link>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="group overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 shadow-sm transition duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg">
              <img
                src={require("../../assets/Services/expertSupport.webp")}
                alt="QuantaRock expert support team"
                className="h-64 w-full rounded-2xl object-cover transition duration-500 group-hover:scale-105 md:h-72"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related Services */}
      <section className="overflow-hidden bg-white px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>Explore more</Eyebrow>
          </div>
          <h2 className="mx-auto mt-3 max-w-3xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
            Explore Our Key Services
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center leading-relaxed text-slate-600">
            Looking for detailed information about our services? Visit our
            dedicated service pages.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                to: "/services/colocation_services",
                title: "Colocation Services",
                description:
                  "Secure data center colocation with reliable power, cooling, and multi-carrier connectivity in Indore and across India.",
              },
              {
                to: "/services/dedicated_servers",
                title: "Dedicated Servers",
                description:
                  "High-performance dedicated servers tailored to your specific requirements with 24/7 support and guaranteed uptime.",
              },
              {
                to: "/services/cloud_services",
                title: "Cloud Services",
                description:
                  "Scalable, secure cloud infrastructure designed for modern applications with flexible pricing and enterprise support.",
              },
            ].map((item, index) => (
              <Reveal
                key={item.to}
                direction={index % 2 === 0 ? "left" : "right"}
                delay={index * 0.1}
                className="h-full"
              >
                <Link
                  to={item.to}
                  aria-label={item.title}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
                >
                  <h3 className="text-xl font-semibold text-[var(--website-primary-color)]">
                    {item.title}
                  </h3>
                  <p className="mt-3 flex-1 leading-relaxed text-slate-600">
                    {item.description}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--website-primary-color)] transition group-hover:gap-3 group-hover:text-[var(--website-secondary-color)]">
                    Learn more
                    <FaArrowRight size={12} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQPage;
